import { ComponentType } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FarewellService } from '@data-terminal/base/app/services/farewell.service';
import { NotificationSendingService } from '@data-terminal/data-access/notifications/notification-sending.service';
import { DataTerminalSessionService } from '@data-terminal/data-access/session-service';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import { EndShiftDialogComponent } from '@data-terminal/feature-dialogs/dialogs/end-shift-dialog/end-shift-dialog.component';
import { SignOffDialogComponent } from '@data-terminal/feature-dialogs/dialogs/sign-off-dialog/sign-off-dialog.component';
import {
    createInitialDialogProcessConfig,
    prepareUserMachinesToSignOff,
    updateDialogProcessConfig,
    updatesOperation,
} from '@data-terminal/feature-dialogs/services/processes/sign-off-process.functions';
import { SignOffService } from '@data-terminal/feature-dialogs/services/sign-off-service/sign-off.service';
import { TransitionService } from '@data-terminal/feature-transition/services';
import { GeneralActivitiesService } from '@data-terminal/feature-workstation-details/services/general-activities/general-activities.service';
import { PureTimeApiService } from '@data-terminal/feature-workstation-details/services/pure-time-api/pure-time-api.service';
import { ApiRequestType } from '@data-terminal/shared-models/api-request-type.enum';
import { WorkstationSignOffError } from '@data-terminal/shared-models/client-api-error';
import { SignOffDialogConfig } from '@data-terminal/shared-models/dialogs/sign-off-dialog';
import { ErrorCode } from '@data-terminal/shared-models/error-correction';
import { SnackbarClass } from '@data-terminal/shared-models/global';
import { Machine, SignOffList, SignOffMachine, UserMachines } from '@data-terminal/shared-models/machine';
import { User } from '@data-terminal/shared-models/session';
import { TimeModeEntry } from '@data-terminal/shared-models/time-mode-entry.interface';
import {
    ClientApiErrorDialogComponent,
    ClientApiErrorDialogData,
    ClientApiErrorDialogResponse,
} from '@data-terminal/ui-smart/dialogs';
import { HplusRedirectionService, SnackBarService } from '@data-terminal/utils/services';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, switchMap, take, tap } from 'rxjs';

const RETRY_COUNT = 15;
const MACHINE_DISPATCH_DELAY = 2000;

export abstract class AbstractSignOffProcessService {
    protected user!: User;

    protected signOffMachines!: SignOffMachine[];

    protected unsubmittedTimeEntries: TimeModeEntry[] = [];

    protected readonly dialogConfig$ = new BehaviorSubject<SignOffDialogConfig>({
        employees: false,
        summary: false,
        operations: false,
        timeTransfer: false,
        workstations: false,
    });
    protected readonly signOffMachines$ = new BehaviorSubject<SignOffMachine[]>([]);

    protected dialogConfirmed = false;

    constructor(
        protected readonly dialog: MatDialog,
        protected readonly signOffService: SignOffService,
        protected readonly router: Router,
        protected readonly activatedRoute: ActivatedRoute,
        protected readonly userMachinesService: UserMachinesService,
        protected readonly notificationservice: NotificationSendingService,
        protected readonly translate: TranslateService,
        protected readonly snackBarService: SnackBarService,
        protected readonly sessionService: DataTerminalSessionService,
        protected readonly hplusRedirectionService: HplusRedirectionService,
        protected readonly pureTimeApiService: PureTimeApiService,
        protected readonly farewellService: FarewellService,
        protected readonly transitionService: TransitionService,
        protected readonly generalActivitiesService: GeneralActivitiesService
    ) {}

    protected abstract prepareStream(
        unsubmittedTimeEntries: TimeModeEntry[],
        user?: User
    ): Observable<SignOffMachine[]>;

    public startProcess(user: User, i18nPrefix: string, endApplication = false): Observable<UserMachines> {
        let i = 0;
        let error = false;
        let errorMsg = '';
        let signOffBody: SignOffMachine[];

        return combineLatest([
            this.userMachinesService.getUserMachines$(),
            this.pureTimeApiService.getUnsubmittedTimeEntries(),
        ]).pipe(
            take(1),
            tap(() => {
                this.user = user;
                this.signOffMachines = prepareUserMachinesToSignOff(
                    this.userMachinesService.getAssignedMachines(),
                    user.userId
                );

                this.signOffMachines$.next(this.signOffMachines);
            }),
            switchMap(([, unsubmittedTimeEntries]) => this.prepareStream(unsubmittedTimeEntries, user)),
            tap((body: SignOffMachine[]) => {
                signOffBody = body;
            }),
            switchMap((body: SignOffMachine[]) => this.signOffService.signOffMachines(body, this.user)),
            tap(() => this.signOffService.triggerSignOffCompleted()),
            tap({
                next: (response) => {
                    this.farewellService.addFarewellRequestItems(
                        this.signOffMachines$.value
                            .filter((signOffMachine) => {
                                return (
                                    signOffMachine.machine.signedOnUsers.find((signedOnUser) => signedOnUser.captain)
                                        ?.userId === this.user.userId
                                );
                            })
                            .map((signOffMachine) => {
                                return {
                                    deviceId: signOffMachine.machineId,
                                    shiftStart: `${signOffMachine.machine.signedOnUsers.find((signedOnUser) => signedOnUser.captain)?.timestamp}`,
                                };
                            })
                    );
                    if (this.farewellService.hasFarewellScreenData()) {
                        this.transitionService.navigateToFarewellPage(endApplication);
                        setTimeout(() => {
                            this.signOffService.dispatchMachines(response);
                        }, MACHINE_DISPATCH_DELAY);
                    }
                },
            }),
            tap(() => {
                const leaveMachines = this.signOffMachines$.value
                    .filter((machine) => machine.assistantsSignOff === 'LEAVE')
                    .map((machine) => machine.machine);
                if (this.dialogConfirmed) {
                    for (const machine of leaveMachines) {
                        const signOffMachine = machine.machineId;
                        const signOffUserList = machine.signedOnUsers
                            .filter((signedOnUser) => signedOnUser.captain === false)
                            .map((signedOnUser) => signedOnUser.userId);
                        this.notificationservice
                            .sendUserSignOffLeaveNotification(
                                `${this.sessionService.getCurrentDataTerminalUser().firstName} ${
                                    this.sessionService.getCurrentDataTerminalUser().lastName
                                }`,
                                signOffMachine,
                                signOffUserList
                            )
                            .subscribe();
                    }
                }
            }),
            tap({
                next: (response) => {
                    i += 1;
                    if (response !== undefined && response !== null) {
                        error = response.serviceError.error;
                        errorMsg = response.serviceError.errorMsg;
                    }
                },
                error: () => {
                    this.snackBarService.openSnackBar(
                        this.translate.instant(`${i18nPrefix}.SNACKBAR.ERROR`),
                        SnackbarClass.ERROR
                    );
                },
                complete: () => {
                    if (error) {
                        if (errorMsg !== '') {
                            const signOffErrors = JSON.parse(errorMsg) as WorkstationSignOffError[];

                            const errorCorrectionBody: SignOffList = {
                                userId: user.userId,
                                signofflist: this.signOffService.omitMachineInSignOffMachines(
                                    signOffErrors.map((e) => e.item)
                                ),
                            };

                            this.openSignOffShiftEndErrorCorrectionDialog(errorCorrectionBody);
                        }
                    } else if (i <= RETRY_COUNT) {
                        if (i > 0) {
                            this.snackBarService.openSnackBar(
                                this.translate.instant(`${i18nPrefix}.SNACKBAR.SUCCESS`),
                                SnackbarClass.SUCCESS
                            );
                        }
                    } else {
                        const errorCorrectionBody: SignOffList = {
                            userId: user.userId,
                            signofflist: this.signOffService.omitMachineInSignOffMachines(signOffBody),
                        };
                        this.openSignOffShiftEndErrorCorrectionDialog(errorCorrectionBody);
                    }
                },
            })
        );
    }

    public getMachineToSignOff(): Observable<SignOffMachine[]> {
        return this.signOffMachines$.asObservable();
    }

    public getConfig(): Observable<SignOffDialogConfig> {
        return this.dialogConfig$.asObservable();
    }

    public onOperationStateChange(signOffMachines: SignOffMachine[]): void {
        this.signOffMachines$.next(signOffMachines);
    }

    public onEmployeesStateChange(signOffMachines: SignOffMachine[]): void {
        const updatedSignOffMachines = signOffMachines.map((el) => updatesOperation(el));
        this.signOffMachines$.next(updatedSignOffMachines);
        this.dialogConfig$.next(
            updateDialogProcessConfig(
                updatedSignOffMachines,
                this.user.userId,
                this.userMachinesService.getAssignedMachines(),
                this.unsubmittedTimeEntries
            )
        );
    }

    public onWorkstationStateChange(machines: Machine[]): void {
        const signOffMachines = prepareUserMachinesToSignOff(machines, this.user.userId);
        this.signOffMachines$.next(signOffMachines);
        this.dialogConfig$.next(
            updateDialogProcessConfig(
                signOffMachines,
                this.user.userId,
                this.userMachinesService.getAssignedMachines(),
                this.unsubmittedTimeEntries
            )
        );
    }

    public markSubmittedTimeEntriesForMachines(machineIds: string[]): void {
        if (!machineIds.length) {
            return;
        }

        const machineIdSet = new Set(machineIds);
        this.unsubmittedTimeEntries = this.unsubmittedTimeEntries.filter((entry) => !machineIdSet.has(entry.deviceId));
        const updatedConfig = updateDialogProcessConfig(
            this.signOffMachines$.value,
            this.user.userId,
            this.userMachinesService.getAssignedMachines(),
            this.unsubmittedTimeEntries
        );
        // Keep the summary step visible once the dialog is open: submitting the last open times
        // would otherwise drop every config flag for a single machine and remove the summary step
        // the user is being advanced to, leaving an empty dialog.
        this.dialogConfig$.next({
            ...updatedConfig,
            summary: updatedConfig.summary || this.dialogConfig$.value.summary,
        });
    }

    public openDialog(
        dialogComponent: ComponentType<EndShiftDialogComponent | SignOffDialogComponent>,
        unsubmittedTimeEntries: TimeModeEntry[],
        isEndShift = false
    ): Observable<SignOffMachine[]> {
        this.unsubmittedTimeEntries = [...unsubmittedTimeEntries];
        const dialogConfig = createInitialDialogProcessConfig(
            this.userMachinesService.getAssignedMachines(),
            this.user.userId,
            unsubmittedTimeEntries,
            isEndShift
        );
        this.dialogConfig$.next(dialogConfig);

        return this.dialog
            .open(dialogComponent, {
                height: '80%',
                width: '680px',
                disableClose: true,
                data: {
                    data: this.userMachinesService.getAssignedMachines(),
                    userId: this.user.userId,
                    otherActivities$: this.generalActivitiesService.getUserActivities$(),
                    unsubmittedTimeEntries: unsubmittedTimeEntries,
                },
            })
            .afterClosed()
            .pipe(
                filter((data) => !!data),
                tap((data) => {
                    this.dialogConfirmed = data;
                }),
                map(() => this.signOffMachines$.value)
            );
    }

    private openSignOffShiftEndErrorCorrectionDialog(body: SignOffList): void {
        this.dialog.open<ClientApiErrorDialogComponent, ClientApiErrorDialogData, ClientApiErrorDialogResponse>(
            ClientApiErrorDialogComponent,
            {
                disableClose: true,
                data: {
                    errorCode: ErrorCode.ERR_SIGNOFF,
                    requestType: ApiRequestType.SIGNOFF,
                    body: JSON.stringify(body),
                },
            }
        );
    }
}
